import { useEffect, useRef } from "react"
import { Loader } from "@/shared/ui/loader"
import { NoTableData } from "@/shared/ui/no-table-data"
import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { nextPage } from "../../model"
import { selectBonusProcessingTransactions, selectBonusProcessingTransactionsLoading } from "../../model/selectors"
import { Table } from "@/shared/ui/table"
import { Link, useNavigate } from "react-router-dom"
import { ColumnDef } from "@tanstack/react-table"
import { BonusAmountCard, selectBonuses } from "@/entities/bonus"
import { IProcessingTransaction } from "@/shared/api/bonuses"
import { ActionButton } from "@/shared/ui/action-button"
import ArrowLeftSvg from '@/shared/assets/images/ArrowBendDownRight.svg'
import UserSvg from '@/shared/assets/images/User.svg'
import CoinsSvg from '@/shared/assets/images/Coins.svg'
import { toLocaleDateTimeString } from "@/shared/lib/date-formatters"

const TransactionTypes: {[key: number]: string} = {
    1: "Списание",
    5: "Начисление"
}

export const ProcessingTransactionsTable = () => {
    const observerRef = useRef<HTMLDivElement>(null)
    const dispatch = useAppDispatch()
    const transactions = useAppSelector(selectBonusProcessingTransactions)
    const transactionsLoading = useAppSelector(selectBonusProcessingTransactionsLoading)

    const observerOptions: IntersectionObserverInit = {
        root: null,
        rootMargin: "0px",
        threshold: 1.0
    }

    const bonuses = useAppSelector(selectBonuses)
    const navigate = useNavigate()

    const columns: ColumnDef<IProcessingTransaction, any>[] = [
        {
            accessorFn: (transaction) => transaction,
            enableSorting: false,
            id: 'bonus',
            cell: info => {
                const transaction: IProcessingTransaction = info.getValue()
                const transactionBonus = bonuses.find(bonus => bonus.id === transaction.bonus_id)
                return (
                    <div className="flex gap-2 items-center pr-4">
                        <div className='w-8 h-8 shrink-0'>
                            <img
                                className="object-contain"
                                src={transactionBonus?.image}
                            />
                        </div>
                        <div className="flex flex-col gap-[2px]">
                            <div className="text-base">
                                {transactionBonus?.symbol}
                            </div>
                            <div className="text-[13px] text-[#828282]">
                                {transactionBonus?.name}
                            </div>
                        </div>
                    </div>
                )
            },
            header: () => <div className="!text-start text-nowrap">Бонус</div>,
        },
        {
            accessorFn: (transaction) => (
                <div className="w-fit uppercase">
                    <BonusAmountCard amountNano={Number(transaction.amount_nano)} />        
                </div>
            ),
            enableSorting: false,
            id: 'amount',
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Сумма транзакции</div>,
        },
        {
            accessorFn: (transaction) => transaction,
            enableSorting: false,
            id: 'accruals',
            cell: info => {
                const transaction: IProcessingTransaction = info.getValue()
                if (!transaction.accruals?.length) {
                    return null
                }

                return (
                    <div className="flex gap-2 items-start">
                        <div className="text-color-primary shrink-0">
                            <CoinsSvg width={20} height={20} />
                        </div>
                        <div className="flex flex-col gap-[2px]">
                            {transaction.accruals.map(accrual => (
                                <div key={accrual.partner_accrual_id + accrual.bonus_id} className="text-nowrap">
                                    {accrual.bonus_name}: {accrual.amount}
                                </div>
                            ))}
                        </div>
                    </div>
                )
            },
            header: () => <div className="!text-start text-nowrap">Начисления</div>,
        },
        {
            accessorFn: (transaction) => (
                <div className="w-fit uppercase font-semibold text-nowrap">
                    {TransactionTypes[transaction.reason_type]}
                </div>
            ),
            enableSorting: false,
            id: 'type',
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Тип операции</div>,
        },
        {
            accessorFn: (transaction) => transaction,
            enableSorting: false,
            id: 'user',
            cell: info => {
                const transaction: IProcessingTransaction = info.getValue()
                return (
                    <div className="flex gap-2 items-center">
                        <div className='relative'>
                            <div className="bg-color-dark text-white rounded-full h-11 w-11 flex items-center justify-center">
                                <UserSvg width={24} height={24} />
                            </div>
                        </div>
                        <div className="flex flex-col gap-[2px]">
                            <Link to={`/users/profile/${transaction.client_id}`} className="text-base hover:underline">
                                {transaction.client_name?.trim() || "—"}
                            </Link>
                            <div className="text-[13px] text-[#828282]">
                                {transaction.telegram_id || "—"}
                            </div>
                        </div>
                    </div>
                )
            },
            header: () => <div className="!text-start text-nowrap">Пользователь</div>,
        },
        {
            accessorFn: (transaction) => transaction,
            enableSorting: false,
            id: 'store',
            cell: info => {
                const transaction: IProcessingTransaction = info.getValue()
                return (
                    <div className="flex gap-2 items-center">
                        {transaction.store_name || "—"}
                    </div>
                )
            },
            header: () => <div className="!text-start text-nowrap">Торговая точка</div>,
        },
        {
            accessorFn: (transaction) => transaction,
            enableSorting: false,
            id: 'address',
            cell: info => {
                const transaction: IProcessingTransaction = info.getValue()
                return (
                    <div className="flex gap-2 items-center">
                        {transaction.store_address || "—"}
                    </div>
                )
            },
            header: () => <div className="!text-start text-nowrap">Адрес</div>,
        },
        {
            accessorFn: (transaction) => (
                <div className="text-nowrap">
                    {toLocaleDateTimeString(transaction.created_date)}
                </div>
            ),
            id: 'date',
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Дата операции</div>,
        },
        {
            accessorFn: (transaction) => (
                <div className="w-full flex justify-end gap-2">
                    <ActionButton
                        onClick={() => navigate(`/bonuses/transactions/${transaction.id}`)}
                        icon={ArrowLeftSvg}
                    />
                </div>
            ),
            enableSorting: false,
            id: 'actions',
            cell: info => info.getValue(),
            header: () => <div />,
        }
    ]

    useEffect(() => {
        const handleObserve = (entries: IntersectionObserverEntry[]) => {
            const [entry] = entries
            if (entry?.isIntersecting) {
                dispatch(nextPage())
            }
        }
        const observer = new IntersectionObserver(handleObserve, observerOptions)
        if (observerRef.current) observer.observe(observerRef.current)
        return () => {
            if (observerRef.current) observer.unobserve(observerRef.current)
        }
    }, [observerRef, observerOptions])

    return (
        <>
            {Boolean(transactions.length) &&
                <div className="table_box">
                    <Table columns={columns} data={transactions} />
                    <div ref={observerRef} id="observer" />
                </div>
            }
            {(!Boolean(transactions.length) && transactionsLoading) &&
                <div className="info_panel">
                    <Loader />
                </div>
            }
            {(!Boolean(transactions.length) && !transactionsLoading) &&
                <NoTableData label="Нет данных о бонусных транзакциях" />
            }
        </>
    )
}
